import { useEffect } from 'react'
import maplibregl from 'maplibre-gl'
import { clusterStructuresGeoJSON, badgeImageExpression } from '../../utils/structures'
import { useMapStore, mapState } from '../../store/mapStore'

// Structures overlay: icon markers for every structure with resolved world
// coordinates. Independent of the mutually-exclusive base layers so it combines
// with any of them; forced on during Construction Mode. Layer + hover popup are
// created once (first time data arrives), then only source data / visibility are
// updated. In Construction Mode own structures drag-to-move (ghost + validation).
export function useStructures(map, { showStructures, constructionMode, infrastructureMode, structuresData, zoomBucket, onStructureMove, regionsData }) {
  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill')) return

    // Force-hidden while the infra tool is active — the two build tools show
    // only their own kind of object so the workspace stays readable.
    const visible = (!!showStructures || !!constructionMode) && !infrastructureMode
    // Same-type icons overlapping at the current zoom merge into one marker with
    // a ×N badge (see clusterStructuresGeoJSON) — zoomBucket in the deps
    // recomputes the grouping when the scale changes. Cluster at the integer
    // bucket zoom (not the live fractional zoom) so the grouping is deterministic.
    const fc = clusterStructuresGeoJSON(visible ? structuresData : [], zoomBucket)

    // Icon size: level 1-7 scales the base size, and the whole thing scales with
    // zoom so markers stay readable close up. Icons are 128px source images.
    const levelFactor = ['interpolate', ['linear'], ['get', 'level'], 1, 0.75, 7, 1.25]
    const iconSizeExpr = [
      'interpolate', ['linear'], ['zoom'],
      2, ['*', 0.08, levelFactor],
      5, ['*', 0.22, levelFactor],
      8, ['*', 0.42, levelFactor],
      10, ['*', 0.60, levelFactor],
    ]

    const src = map.current.getSource('structures')
    if (!src) {
      map.current.addSource('structures', { type: 'geojson', data: fc })
      map.current.addLayer({
        id: 'structures-points',
        type: 'symbol',
        source: 'structures',
        layout: {
          // icon_key resolves type+specialisation to the PNG variant
          // (pr_usine_t, pr_tech_a, …) — built in clusterStructuresGeoJSON.
          'icon-image': ['concat', 'structure-icon-', ['get', 'icon_key']],
          'icon-size': iconSizeExpr,
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
        },
      })

      // ×N badge on merged markers (top-right of the icon)
      map.current.addLayer({
        id: 'structures-count-badge',
        type: 'symbol',
        source: 'structures',
        filter: ['>', ['get', 'count'], 1],
        layout: {
          'icon-image': badgeImageExpression,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 2, 0.45, 8, 0.7],
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom-left',
          'icon-offset': [30, -70],
        },
      })

      // Ghost marker shown under the cursor while dragging a structure.
      map.current.addSource('structure-drag-ghost', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.current.addLayer({
        id: 'structure-drag-ghost',
        type: 'symbol',
        source: 'structure-drag-ghost',
        layout: {
          'icon-image': ['concat', 'structure-icon-', ['get', 'icon_key']],
          'icon-size': iconSizeExpr,
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
        },
        paint: { 'icon-opacity': 0.6 },
      })

      const structurePopup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'region-hover-popup structure-hover-popup',
      })

      map.current.on('mousemove', 'structures-points', (e) => {
        if (mapState().structDragActive) return
        if (!e.features || e.features.length === 0) return
        const p = e.features[0].properties
        const specLabel = p.specialisation && p.specialisation !== 'NA' ? ` · ${p.specialisation}` : ''
        const isCluster = p.count > 1
        // A stack can mix countries (clustering only groups by icon+position),
        // so a stack is draggable if at least one member is ours — we'll pick
        // that member out of the stack on mousedown.
        const ownedMember = isCluster
          ? JSON.parse(p.members).find((m) => m.country_id === mapState().ownedCountryId)
          : null
        const movable = mapState().constructionModeActive && (isCluster ? !!ownedMember : p.country_id === mapState().ownedCountryId)
        // Housing has no meaningful level (always 1) — its capacity is what
        // describes it, so show that instead.
        const detail = p.type === 'Logement'
          ? `${Number(p.capacity || 0).toLocaleString('fr-FR')} hab.`
          : `Niveau ${p.level}`
        const html = isCluster
          ? `
            <div style="font-size: 12px; padding: 4px;">
              <strong>${p.count} × ${p.type}</strong>${specLabel}<br/>
              ${p.type === 'Logement'
                ? (p.region_name || `Région #${p.region_id}`)
                : `Niveaux ${p.min_level}${p.max_level !== p.min_level ? `–${p.max_level}` : ''} · ${p.region_name || `Région #${p.region_id}`}`}<br/>
              <em>${movable ? `Glisser pour déplacer #${ownedMember.id}` : 'Zoomez pour les séparer'}</em>
            </div>
          `
          : `
            <div style="font-size: 12px; padding: 4px;">
              <strong>#${p.id}</strong> — ${p.type}${specLabel}<br/>
              ${detail} · ${p.region_name || `Région #${p.region_id}`}
              ${movable ? '<br/><em>Glisser pour déplacer</em>' : ''}
            </div>
          `
        structurePopup.setLngLat(e.lngLat).setHTML(html).addTo(map.current)
        map.current.getCanvas().style.cursor = movable ? 'grab' : 'pointer'
      })

      map.current.on('mouseleave', 'structures-points', () => {
        if (mapState().structDragActive) return
        structurePopup.remove()
        map.current.getCanvas().style.cursor = (mapState().planModeActive || mapState().constructionModeActive) ? 'crosshair' : ''
      })

      // Drag & drop: in Construction Mode, press on one of your own structures
      // and drag to move it. A translucent ghost follows the cursor; on release
      // the new position is validated (must stay inside the structure's region)
      // and persisted by the parent handler.
      map.current.on('mousedown', 'structures-points', (e) => {
        if (!mapState().constructionModeActive || !onStructureMove) return
        if (e.originalEvent.button !== 0) return
        if (!e.features || e.features.length === 0) return
        const raw = e.features[0].properties
        // Stacked markers: drag the caller's own structure out of the stack
        // rather than refusing outright — the stack can't split by zooming
        // alone when structures sit at literally the same coordinates.
        const target = raw.count > 1
          ? JSON.parse(raw.members).find((m) => m.country_id === mapState().ownedCountryId)
          : raw
        if (!target || target.country_id !== mapState().ownedCountryId) return

        e.preventDefault() // keep the map from panning during the drag
        useMapStore.setState({ structDragActive: true })
        structurePopup.remove()
        map.current.getCanvas().style.cursor = 'grabbing'

        const ghostSrc = map.current.getSource('structure-drag-ghost')
        const setGhost = (lngLat) => ghostSrc.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lngLat.lng, lngLat.lat] },
            properties: { icon_key: target.icon_key, level: target.level },
          }],
        })
        setGhost(e.lngLat)

        const onMove = (ev) => setGhost(ev.lngLat)
        const onUp = (ev) => {
          map.current.off('mousemove', onMove)
          useMapStore.setState({ structDragActive: false })
          useMapStore.setState({ suppressNextMapClick: Date.now() })
          ghostSrc.setData({ type: 'FeatureCollection', features: [] })
          map.current.getCanvas().style.cursor = 'crosshair'
          const regionFeats = map.current.queryRenderedFeatures(ev.point, { layers: ['regions-fill'] })
          onStructureMove(target, ev.lngLat, regionFeats[0]?.properties || null)
        }
        map.current.on('mousemove', onMove)
        map.current.once('mouseup', onUp)
      })
    } else {
      src.setData(fc)
    }

    const vis = visible ? 'visible' : 'none'
    if (map.current.getLayer('structures-points')) {
      map.current.setLayoutProperty('structures-points', 'visibility', vis)
    }
    if (map.current.getLayer('structures-count-badge')) {
      map.current.setLayoutProperty('structures-count-badge', 'visibility', vis)
    }
  }, [map, structuresData, showStructures, constructionMode, infrastructureMode, regionsData, zoomBucket, onStructureMove])
}
